/* eslint-disable @typescript-eslint/no-explicit-any */

import Dexie, { type Table } from "dexie";

import { exportDB } from "dexie-export-import";

import {
  forgetLegacyGalleryAudio,
  isLegacyRemoteGalleryTrack,
} from "../entities/Playlist/legacyGalleryTracks";

/**
 * Fuentes remotas que la migración v29 borró de la playlist: su audio cacheado
 * se limpia después de abrir la base (fuera de la transacción del upgrade).
 */
const purgedLegacyGallerySources: string[] = [];

import FilesService from "../infrastructure/Services/FilesService";

import type { IAlertAssetDB, IAlertLibraryDB } from "../interfaces/Alert";

import type { IAlbumDB, IArtistDB, ITrackDB } from "../interfaces/Artists";

import type { IDesignLibraryDB } from "../interfaces/Design";

import type { IConfigDB } from "../interfaces/Config";

import type { IVideoDB } from "../interfaces/Database";

import type { IImageDB } from "../interfaces/Images";

import type { IOverlayAssetDB, IOverlayLibraryDB } from "../interfaces/Overlay";

import type {
  IOverlayCounterDB,
  IOverlayEventStatDB,
} from "../interfaces/OverlayStats";

import type { IPanelAssetDB, IPanelLibraryDB } from "../interfaces/Panel";

import type { IPlaylistDB } from "../interfaces/Playlist";

import type { ISceneDB } from "../interfaces/Scenes";

import type { IStreamOutputDB } from "../interfaces/Streaming";

import type { ITeamDB } from "../interfaces/Team";

import type { IUIElement } from "../interfaces/UI";

import type { IUserDB, IUserLang } from "../interfaces/User";

import type { IWidgetDB } from "../interfaces/Widgets";
import { localApiPort } from "../lib/localApi";

import { createCoalescedSaver } from "./coalescedSave";

class RadioDatabase extends Dexie {
  artists!: Table<IArtistDB>;

  albums!: Table<IAlbumDB>;

  tracks!: Table<ITrackDB>;

  user!: Table<IUserDB>;

  config!: Table<IConfigDB>;

  team!: Table<ITeamDB>;

  streamOutput!: Table<IStreamOutputDB>;

  playlist!: Table<IPlaylistDB>;

  videos!: Table<IVideoDB>;

  images!: Table<IImageDB>;

  scenes!: Table<ISceneDB>;

  widgets!: Table<IWidgetDB>;

  ui!: Table<IUIElement>;

  overlayLibrary!: Table<IOverlayLibraryDB>;

  overlayAssets!: Table<IOverlayAssetDB>;

  overlayCounters!: Table<IOverlayCounterDB>;

  overlayEventStats!: Table<IOverlayEventStatDB>;

  alertLibrary!: Table<IAlertLibraryDB>;

  alertAssets!: Table<IAlertAssetDB>;

  panelLibrary!: Table<IPanelLibraryDB>;

  panelAssets!: Table<IPanelAssetDB>;

  installedDesigns!: Table<IDesignLibraryDB>;

  constructor() {
    super("Radio");

    this.version(20).stores({
      user: "++id, &account, email, &name, lastName, cellphone, avatar",

      config: "++id, userId, configuration",

      artists: "++id, &name, cover, biography, slug",

      team: "++id, mics, cams",

      albums: "++id, name, cover, artistId, slug",

      tracks: "++id, name, source, artistId, albumId, slug",

      streamOutput: "++id, &name, stream, configuration",

      playlist: "++id, name, source, number, artistId, albumId",

      videos: "++id, &name, thumbnail, directory",

      images: "++id, &name, slug, directory",

      scenes: "++id, &name, order, model, active",

      widgets: "++id, name, type, configuration",

      ui: "++id, &elementId, elementType, layout, metadata",
    });

    this.version(21)

      .stores({
        user: "++id, &account, email, &name, lastName, cellphone, avatar",

        config: "++id, userId, configuration",

        artists: "++id, &name, cover, biography, slug",

        team: "++id, mics, cams",

        streamOutput:
          "++id, [userId+name], userId, name, stream, configuration",

        playlist: "++id, name, source, number, artistId, albumId",

        videos: "++id, &name, thumbnail, directory",

        images: "++id, &name, slug, directory",

        scenes: "++id, &name, order, model, active",

        widgets: "++id, name, type, configuration",

        ui: "++id, &elementId, elementType, layout, metadata",
      })

      .upgrade((tx) => {
        const currentUserId =
          localStorage.getItem("@Eriol/system/userId") || "__legacy__";

        return tx

          .table("streamOutput")

          .toCollection()

          .modify((record: { userId?: string }) => {
            if (!record.userId) {
              record.userId = currentUserId;
            }
          });
      });

    this.version(22).stores({
      user: "++id, &account, email, &name, lastName, cellphone, avatar",

      config: "++id, userId, configuration",

      artists: "++id, &name, cover, biography, slug",

      team: "++id, mics, cams",

      streamOutput: "++id, [userId+name], userId, name, stream, configuration",

      playlist: "++id, name, source, number, artistId, albumId",

      videos: "++id, &name, thumbnail, directory",

      images: "++id, &name, slug, directory",

      scenes: "++id, &name, order, model, active",

      widgets: "++id, name, type, configuration",

      ui: "++id, &elementId, elementType, layout, metadata",

      overlayLibrary:
        "id, slug, status, [meta.category], [meta.authorId], updatedAt",

      overlayAssets: "id, overlayId, kind",
    });

    this.version(23).stores({
      user: "++id, &account, email, &name, lastName, cellphone, avatar",

      config: "++id, userId, configuration",

      artists: "++id, &name, cover, biography, slug",

      team: "++id, mics, cams",

      streamOutput: "++id, [userId+name], userId, name, stream, configuration",

      playlist: "++id, name, source, number, artistId, albumId",

      videos: "++id, &name, thumbnail, directory",

      images: "++id, &name, slug, directory",

      scenes: "++id, &name, order, model, active",

      widgets: "++id, name, type, configuration",

      ui: "++id, &elementId, elementType, layout, metadata",

      overlayLibrary:
        "id, slug, status, [meta.category], [meta.authorId], updatedAt",

      overlayAssets: "id, overlayId, kind",

      overlayCounters:
        "[accountId+counterName], accountId, counterName, updatedAt",

      overlayEventStats:
        "[accountId+kind+dayBucket], accountId, kind, dayBucket, updatedAt",
    });

    this.version(24).stores({
      user: "++id, &account, email, &name, lastName, cellphone, avatar",

      config: "++id, userId, configuration",

      artists: "++id, &name, cover, biography, slug",

      team: "++id, mics, cams",

      streamOutput: "++id, [userId+name], userId, name, stream, configuration",

      playlist: "++id, name, source, number, artistId, albumId",

      videos: "++id, &name, thumbnail, directory",

      images: "++id, &name, slug, directory",

      scenes: "++id, &name, order, model, active",

      widgets: "++id, name, type, configuration",

      ui: "++id, &elementId, elementType, layout, metadata",

      overlayLibrary:
        "id, slug, status, [meta.category], [meta.authorId], updatedAt",

      overlayAssets: "id, overlayId, kind",

      overlayCounters:
        "[accountId+counterName], accountId, counterName, updatedAt",

      overlayEventStats:
        "[accountId+kind+dayBucket], accountId, kind, dayBucket, updatedAt",

      alertLibrary:
        "id, slug, status, [meta.triggerKind], [meta.license], [meta.authorId], updatedAt",

      alertAssets: "id, alertId, kind",
    });

    // Dexie can't change a store's primary key in-place

    // ("UpgradeError Not yet support for changing primary key"). The

    // workaround is to drop `overlayEventStats` in v25 and recreate it

    // with the new compound key in v26. Stats reset, but the schema is

    // consistent going forward.

    this.version(25).stores({
      user: "++id, &account, email, &name, lastName, cellphone, avatar",

      config: "++id, userId, configuration",

      artists: "++id, &name, cover, biography, slug",

      team: "++id, mics, cams",

      streamOutput: "++id, [userId+name], userId, name, stream, configuration",

      playlist: "++id, name, source, number, artistId, albumId",

      videos: "++id, &name, thumbnail, directory",

      images: "++id, &name, slug, directory",

      scenes: "++id, &name, order, model, active",

      widgets: "++id, name, type, configuration",

      ui: "++id, &elementId, elementType, layout, metadata",

      overlayLibrary:
        "id, slug, status, [meta.category], [meta.authorId], updatedAt",

      overlayAssets: "id, overlayId, kind",

      overlayCounters:
        "[accountId+counterName], accountId, counterName, updatedAt",

      // null = delete this store on upgrade

      overlayEventStats: null,

      alertLibrary:
        "id, slug, status, [meta.triggerKind], [meta.license], [meta.authorId], updatedAt",

      alertAssets: "id, alertId, kind",
    });

    this.version(26).stores({
      user: "++id, &account, email, &name, lastName, cellphone, avatar",

      config: "++id, userId, configuration",

      artists: "++id, &name, cover, biography, slug",

      team: "++id, mics, cams",

      streamOutput: "++id, [userId+name], userId, name, stream, configuration",

      playlist: "++id, name, source, number, artistId, albumId",

      videos: "++id, &name, thumbnail, directory",

      images: "++id, &name, slug, directory",

      scenes: "++id, &name, order, model, active",

      widgets: "++id, name, type, configuration",

      ui: "++id, &elementId, elementType, layout, metadata",

      overlayLibrary:
        "id, slug, status, [meta.category], [meta.authorId], updatedAt",

      overlayAssets: "id, overlayId, kind",

      overlayCounters:
        "[accountId+counterName], accountId, counterName, updatedAt",

      overlayEventStats:
        "[accountId+kind+platform+dayBucket], accountId, kind, platform, dayBucket, updatedAt",

      alertLibrary:
        "id, slug, status, [meta.triggerKind], [meta.license], [meta.authorId], updatedAt",

      alertAssets: "id, alertId, kind",
    });

    this.version(27).stores({
      user: "++id, &account, email, &name, lastName, cellphone, avatar",

      config: "++id, userId, configuration",

      artists: "++id, &name, cover, biography, slug",

      team: "++id, mics, cams",

      streamOutput: "++id, [userId+name], userId, name, stream, configuration",

      playlist: "++id, name, source, number, artistId, albumId",

      videos: "++id, &name, thumbnail, directory",

      images: "++id, &name, slug, directory",

      scenes: "++id, &name, order, model, active",

      widgets: "++id, name, type, configuration",

      ui: "++id, &elementId, elementType, layout, metadata",

      overlayLibrary:
        "id, slug, status, [meta.category], [meta.authorId], updatedAt",

      overlayAssets: "id, overlayId, kind",

      overlayCounters:
        "[accountId+counterName], accountId, counterName, updatedAt",

      overlayEventStats:
        "[accountId+kind+platform+dayBucket], accountId, kind, platform, dayBucket, updatedAt",

      alertLibrary:
        "id, slug, status, [meta.triggerKind], [meta.license], [meta.authorId], updatedAt",

      alertAssets: "id, alertId, kind",

      panelLibrary:
        "id, slug, status, [meta.category], [meta.authorId], updatedAt",

      panelAssets: "id, panelId, kind",
    });

    // v28: widget design marketplace — installed designs keyed by widgetType
    // so each per-widget store lists its own installs cheaply. Carries every
    // v27 store forward unchanged (Dexie requires the full schema per version).
    this.version(28).stores({
      user: "++id, &account, email, &name, lastName, cellphone, avatar",

      config: "++id, userId, configuration",

      artists: "++id, &name, cover, biography, slug",

      team: "++id, mics, cams",

      streamOutput: "++id, [userId+name], userId, name, stream, configuration",

      playlist: "++id, name, source, number, artistId, albumId",

      videos: "++id, &name, thumbnail, directory",

      images: "++id, &name, slug, directory",

      scenes: "++id, &name, order, model, active",

      widgets: "++id, name, type, configuration",

      ui: "++id, &elementId, elementType, layout, metadata",

      overlayLibrary:
        "id, slug, status, [meta.category], [meta.authorId], updatedAt",

      overlayAssets: "id, overlayId, kind",

      overlayCounters:
        "[accountId+counterName], accountId, counterName, updatedAt",

      overlayEventStats:
        "[accountId+kind+platform+dayBucket], accountId, kind, platform, dayBucket, updatedAt",

      alertLibrary:
        "id, slug, status, [meta.triggerKind], [meta.license], [meta.authorId], updatedAt",

      alertAssets: "id, alertId, kind",

      panelLibrary:
        "id, slug, status, [meta.category], [meta.authorId], updatedAt",

      panelAssets: "id, panelId, kind",

      installedDesigns:
        "id, slug, widgetType, status, [meta.category], [meta.authorId]",
    });

    // v29: se retiró la galería "Música libre" (#737). Las pistas que agregó
    // tenían como `source` la URL remota del proveedor y dependían del proxy
    // local que ya no existe (la CSP del desktop no permite media remoto): sin
    // ellas no queda ninguna pista sin reproducir ni crédito CC-BY sin mostrar.
    // Mismo esquema que v28.
    this.version(29)
      .stores({
        user: "++id, &account, email, &name, lastName, cellphone, avatar",

        config: "++id, userId, configuration",

        artists: "++id, &name, cover, biography, slug",

        team: "++id, mics, cams",

        streamOutput: "++id, [userId+name], userId, name, stream, configuration",

        playlist: "++id, name, source, number, artistId, albumId",

        videos: "++id, &name, thumbnail, directory",

        images: "++id, &name, slug, directory",

        scenes: "++id, &name, order, model, active",

        widgets: "++id, name, type, configuration",

        ui: "++id, &elementId, elementType, layout, metadata",

        overlayLibrary:
          "id, slug, status, [meta.category], [meta.authorId], updatedAt",

        overlayAssets: "id, overlayId, kind",

        overlayCounters:
          "[accountId+counterName], accountId, counterName, updatedAt",

        overlayEventStats:
          "[accountId+kind+platform+dayBucket], accountId, kind, platform, dayBucket, updatedAt",

        alertLibrary:
          "id, slug, status, [meta.triggerKind], [meta.license], [meta.authorId], updatedAt",

        alertAssets: "id, alertId, kind",

        panelLibrary:
          "id, slug, status, [meta.category], [meta.authorId], updatedAt",

        panelAssets: "id, panelId, kind",

        installedDesigns:
          "id, slug, widgetType, status, [meta.category], [meta.authorId]",
      })
      .upgrade(async (tx) => {
        const table = tx.table("playlist");
        const legacy = (await table.toArray()).filter(isLegacyRemoteGalleryTrack);
        if (legacy.length === 0) return;
        purgedLegacyGallerySources.push(
          ...legacy.map((track) => String(track.source)),
        );
        await table.bulkDelete(legacy.map((track) => track.id));
      });
  }
}

let db: RadioDatabase | null = null;

export type DatabaseDictionary = RadioDatabase;

export const createDatabase = async (bucketIndexedDB?: IDBFactory) => {
  db = new RadioDatabase();

  if (bucketIndexedDB) {
    // Usar el IndexedDB del bucket específico

    (Dexie as any).dependencies.indexedDB = bucketIndexedDB;
  }

  const result = (await db.open()) as DatabaseDictionary;

  // v29 borró pistas de la galería retirada: su audio cacheado se suelta acá,
  // ya fuera de la transacción del upgrade y sin bloquear el arranque.
  if (purgedLegacyGallerySources.length > 0) {
    void forgetLegacyGalleryAudio(purgedLegacyGallerySources.splice(0));
  }

  // Restaurar el IndexedDB global si estábamos usando un bucket

  if (bucketIndexedDB) {
    (Dexie as any).dependencies.indexedDB = indexedDB;
  }

  return result;
};

export const deleteDatabase = async () => {
  try {
    if (db) {
      await db.delete();

      db = null;

      return true;
    }

    const tempDb = new Dexie("Radio");

    await tempDb.delete();

    return true;
  } catch (error) {
    console.log(error);

    return false;
  }
};

export const openDatabase = async () => {
  if (!db) {
    return await createDatabase();
  }

  return db;
};

export const existDatabase = async (): Promise<boolean> => {
  const exists = await Dexie.exists("Radio");

  return exists;
};

export const resolveCurrentWorkspaceId = async (): Promise<string | null> => {
  let workspaceId = localStorage.getItem("@Eriol/currentWorkspaceId");

  if (workspaceId) return workspaceId;

  const port = localApiPort();

  try {
    const res = await fetch(`http://localhost:${port}/api/workspaces`);

    if (res.ok) {
      const workspaces = await res.json();

      const current = workspaces?.data?.find(
        (ws: any) => ws.current || ws.selected,
      );

      if (current?.id) {
        workspaceId = String(current.id);

        localStorage.setItem("@Eriol/currentWorkspaceId", workspaceId);

        return workspaceId;
      }
    }
  } catch (err) {
    console.warn("[DB] Failed to auto-resolve workspaceId", err);
  }

  return null;
};

export const exportDatabase = async () => {
  if (!db) return;

  const blob = await exportDB(db);

  return blob;
};

interface SaveSnapshotOptions {
  /** If true, also saves station/tables.json in the workspace folder (only on workspace creation) */

  saveBaseTemplate?: boolean;
}

export const saveSnapshotDatabase = async (
  database?: RadioDatabase,

  _legacyPath?: string,

  _options?: SaveSnapshotOptions,
) => {
  const db = database || (await openDatabase());

  if (!db) {
    throw new Error("Database not initialized");
  }

  const blob = await exportDB(db);

  const port = localApiPort();

  // Resolve workspaceId using shared helper

  const workspaceId = await resolveCurrentWorkspaceId();

  // 1. Save per-user snapshot to extension DB (encrypted)

  if (workspaceId) {
    const formData = new FormData();

    formData.append("workspaceId", workspaceId);

    formData.append("snapshot", blob, "tables.json");

    try {
      const res = await fetch(
        `http://localhost:${port}/api/workspaces/snapshot`,
        {
          method: "POST",

          body: formData,
        },
      );

      if (!res.ok) {
        console.warn("[DB] Per-user snapshot save failed", res.status);
      }
    } catch (err) {
      console.warn("[DB] Per-user snapshot endpoint unreachable", err);
    }
  }

  // 2. Sync state back to the workspace folder's station/tables.json. This
  // keeps the file as the source of truth for: (a) the workspace list
  // metadata reader on the extension (userType, sceneCount) which only
  // parses the on-disk template, (b) portability when copying the folder
  // to another machine, and (c) recovery if the encrypted snapshot is
  // lost (e.g. user wipes Roaming). Previously this only ran on workspace
  // creation, which left tables.json stale after every subsequent change.

  const filesService = new FilesService("api");

  const uploadPath = _legacyPath || "station";

  try {
    await filesService.uploadFile(uploadPath, "tables.json", blob);
  } catch (err) {
    console.error("[DB] tables.json upload failed", err);
  }
};

// Snapshot AGRUPADO para las ediciones frecuentes del editor (drag/resize de áreas, texto de
// elementos): exportar toda la IndexedDB y subirla dos veces cada vez que se suelta el mouse era
// trabajo muerto. Espera SNAPSHOT_DEBOUNCE_MS desde la última edición, coalesce las intermedias y si ya hay
// un guardado en vuelo encadena UNO al final. `pagehide` fuerza el guardado pendiente para no
// perder la última edición al cerrar.
export const SNAPSHOT_DEBOUNCE_MS = 1500;

const snapshotSaver = createCoalescedSaver(
  () => saveSnapshotDatabase(),
  SNAPSHOT_DEBOUNCE_MS,
  (error) => console.warn("[DB] snapshot agrupado falló", error),
);

export const scheduleSnapshotSave = (): void => snapshotSaver.schedule();

export const flushSnapshotSave = (): Promise<void> => snapshotSaver.flush();

// Una sola vez por ventana: con HMR el módulo puede evaluarse de nuevo y, sin la marca, cada
// evaluación sumaría otro handler (y otro export de la DB al cerrar).
if (typeof window !== "undefined") {
  const marker = window as unknown as { __eriolSnapshotFlushBound?: boolean };

  if (!marker.__eriolSnapshotFlushBound) {
    marker.__eriolSnapshotFlushBound = true;

    window.addEventListener("pagehide", () => {
      if (snapshotSaver.pending()) void snapshotSaver.flush();
    });
  }
}

export const createAndSaveSnapshotInBucket = async (
  workspaceId: string,
  workspacePath: string,
) => {
  let bucket: any = null;

  let tempDb: RadioDatabase | null = null;

  const api = (navigator as any).storageBuckets;

  const previousDb = db;

  try {
    if (!api || typeof api.open !== "function") {
      throw new Error("Storage Buckets API not supported");
    }

    const bucketName = `workspace-temp-${workspaceId}`;

    bucket = await api.open(bucketName, {
      durability: "strict",

      persisted: false,
    });

    const originalIndexedDB = (Dexie as any).dependencies.indexedDB;

    (Dexie as any).dependencies.indexedDB = bucket.indexedDB;

    try {
      tempDb = new RadioDatabase();

      await tempDb.open();
    } finally {
      (Dexie as any).dependencies.indexedDB = originalIndexedDB;
    }

    const userId = localStorage.getItem("@Eriol/system/userId");

    const i18nLanguage = localStorage.getItem("user-language") || "en";

    if (userId && tempDb) {
      await tempDb.user.add({
        account: userId,

        lang: i18nLanguage as IUserLang,
      } as IUserDB);
    }

    await saveSnapshotDatabase(tempDb, workspacePath + "\\station\\", {
      saveBaseTemplate: true,
    });

    return true;
  } catch (error) {
    console.error("Error creating snapshot in bucket:", error);

    throw error;
  } finally {
    // Restaurar la DB actual

    db = previousDb;

    // Limpiar: cerrar la base de datos temporal

    if (tempDb) {
      await tempDb.close();
    }

    if (bucket && api) {
      try {
        const bucketName = `workspace-temp-${workspaceId}`;

        await api.delete(bucketName);
      } catch (error) {
        console.error("Error deleting temporary bucket:", error);
      }
    }
  }
};
